/**
 * Pooled struct-of-arrays bullet store. The GC never sees a bullet: state
 * lives in typed-array columns, swap-remove keeps [0, count) dense, and the
 * whole update is index arithmetic with zero steady-state allocation.
 *
 * Indices are valid only until the next despawn — nothing may hold one across
 * frames (homing reads the player from update args instead).
 */

export const Mod = {
  None: 0,
  /** p0 = accel px/s², p2 = speed cap (approached from either side). */
  Accel: 1,
  /** p0 = curve rad/s (velocity rotation). */
  Curve: 2,
  /** p0 = max turn rad/s toward player, p1 = homing duration in steps. */
  Homing: 3,
  /** p0 = gravity px/s² on vy. */
  Gravity: 4,
  /** p1 = step at which to re-aim at the player, p2 = new speed. */
  DelayedAim: 5,
} as const;

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export class BulletPool {
  count = 0;

  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly px: Float32Array;
  readonly py: Float32Array;
  readonly vx: Float32Array;
  readonly vy: Float32Array;
  readonly radius: Float32Array;
  readonly scale: Float32Array;
  readonly p0: Float32Array;
  readonly p1: Float32Array;
  readonly p2: Float32Array;
  readonly age: Uint16Array;
  readonly life: Uint16Array;
  readonly modKind: Uint8Array;
  readonly kind: Uint8Array;

  /** Optional wall test (set when a floor's TileMap exists). */
  wallTest: ((x: number, y: number) => boolean) | null = null;

  constructor(readonly capacity: number) {
    this.x = new Float32Array(capacity);
    this.y = new Float32Array(capacity);
    this.px = new Float32Array(capacity);
    this.py = new Float32Array(capacity);
    this.vx = new Float32Array(capacity);
    this.vy = new Float32Array(capacity);
    this.radius = new Float32Array(capacity);
    this.scale = new Float32Array(capacity);
    this.p0 = new Float32Array(capacity);
    this.p1 = new Float32Array(capacity);
    this.p2 = new Float32Array(capacity);
    this.age = new Uint16Array(capacity);
    this.life = new Uint16Array(capacity);
    this.modKind = new Uint8Array(capacity);
    this.kind = new Uint8Array(capacity);
  }

  /** Returns the new index, or -1 when full (drop, never grow). */
  spawn(
    x: number, y: number, angle: number, speed: number,
    kind: number, radius: number, lifeSteps: number, scale = 1,
  ): number {
    if (this.count >= this.capacity) return -1;
    const i = this.count++;
    this.x[i] = x;
    this.y[i] = y;
    this.px[i] = x;
    this.py[i] = y;
    this.vx[i] = Math.cos(angle) * speed;
    this.vy[i] = Math.sin(angle) * speed;
    this.radius[i] = radius;
    this.scale[i] = scale;
    this.age[i] = 0;
    this.life[i] = lifeSteps;
    this.modKind[i] = Mod.None;
    this.kind[i] = kind;
    return i;
  }

  setMod(i: number, mod: number, p0: number, p1: number, p2: number): void {
    this.modKind[i] = mod;
    this.p0[i] = p0;
    this.p1[i] = p1;
    this.p2[i] = p2;
  }

  despawn(i: number): void {
    const last = --this.count;
    if (i === last) return;
    this.x[i] = this.x[last]!;
    this.y[i] = this.y[last]!;
    this.px[i] = this.px[last]!;
    this.py[i] = this.py[last]!;
    this.vx[i] = this.vx[last]!;
    this.vy[i] = this.vy[last]!;
    this.radius[i] = this.radius[last]!;
    this.scale[i] = this.scale[last]!;
    this.p0[i] = this.p0[last]!;
    this.p1[i] = this.p1[last]!;
    this.p2[i] = this.p2[last]!;
    this.age[i] = this.age[last]!;
    this.life[i] = this.life[last]!;
    this.modKind[i] = this.modKind[last]!;
    this.kind[i] = this.kind[last]!;
  }

  clear(): void {
    this.count = 0;
  }

  update(dt: number, playerX: number, playerY: number, bounds: Bounds): void {
    const { x, y, px, py, vx, vy, age, life, modKind, p0, p1, p2 } = this;
    for (let i = 0; i < this.count; i++) {
      px[i] = x[i]!;
      py[i] = y[i]!;

      const mod = modKind[i]!;
      if (mod !== Mod.None) {
        if (mod === Mod.Accel) {
          const s = Math.hypot(vx[i]!, vy[i]!) || 1;
          let ns = s + p0[i]! * dt;
          const cap = p2[i]!;
          ns = p0[i]! > 0 ? Math.min(ns, cap) : Math.max(ns, cap);
          if (ns < 0) ns = 0;
          const f = ns / s;
          vx[i] = vx[i]! * f;
          vy[i] = vy[i]! * f;
        } else if (mod === Mod.Curve) {
          const a = p0[i]! * dt;
          const c = Math.cos(a);
          const s = Math.sin(a);
          const ox = vx[i]!;
          vx[i] = ox * c - vy[i]! * s;
          vy[i] = ox * s + vy[i]! * c;
        } else if (mod === Mod.Homing) {
          if (age[i]! < p1[i]!) {
            const speed = Math.hypot(vx[i]!, vy[i]!) || 1;
            const cur = Math.atan2(vy[i]!, vx[i]!);
            const want = Math.atan2(playerY - y[i]!, playerX - x[i]!);
            let d = want - cur;
            while (d > Math.PI) d -= Math.PI * 2;
            while (d < -Math.PI) d += Math.PI * 2;
            const maxTurn = p0[i]! * dt;
            const turn = d > maxTurn ? maxTurn : d < -maxTurn ? -maxTurn : d;
            const na = cur + turn;
            vx[i] = Math.cos(na) * speed;
            vy[i] = Math.sin(na) * speed;
          }
        } else if (mod === Mod.Gravity) {
          vy[i] = vy[i]! + p0[i]! * dt;
        } else if (mod === Mod.DelayedAim) {
          if (age[i]! === p1[i]!) {
            const na = Math.atan2(playerY - y[i]!, playerX - x[i]!);
            const sp = p2[i]!;
            vx[i] = Math.cos(na) * sp;
            vy[i] = Math.sin(na) * sp;
          }
        }
      }

      const nx = x[i]! + vx[i]! * dt;
      const ny = y[i]! + vy[i]! * dt;
      x[i] = nx;
      y[i] = ny;

      age[i] = age[i]! + 1;
      if (
        age[i]! >= life[i]! ||
        nx < bounds.minX || nx > bounds.maxX || ny < bounds.minY || ny > bounds.maxY ||
        (this.wallTest !== null && this.wallTest(nx, ny))
      ) {
        this.despawn(i);
        i--; // re-process the swapped-in bullet
      }
    }
  }
}
